import type { WalkthroughStep } from './types';

// The OEM+ demo spine — the five-stop executive path through the proof of
// concept, in the order it is presented: the cost position, what is driving
// it, the machine behind the biggest exposure, and the money it moves.
// Gated behind `?demo=true` (see chrome/Walkthrough.tsx). Captions are
// rendered in the bottom-right walkthrough chip and read in the demo's own
// voice — they are spoken, not documentation.
//
// `hash` is matched against `pathname + search` by useWalkthroughSync; keep
// these in step with the routes in oem-nav.ts. `persona` is the FOUNDATION
// persona (Admin | Editor | Reviewer | Viewer), not the OEM persona — the
// whole path runs as Admin so nothing is role-gated mid-demo. `breadcrumb`
// mirrors `crumbFor()` for reference; the chrome derives its own.
export const STEPS: WalkthroughStep[] = [
  {
    hash: '/',
    persona: 'Admin',
    caption:
      'Every dollar the fleet costs, in one place — the picture no single source system can give you.',
    breadcrumb: [{ label: 'Home', hash: '/' }],
  },
  {
    hash: '/issues',
    persona: 'Admin',
    caption:
      'What is driving that number right now: live alerts and incidents, ranked by exposure.',
    breadcrumb: [
      { label: 'Home', hash: '/' },
      { label: 'Issues', hash: '/issues', static: true },
      { label: 'Live', hash: '/issues' },
    ],
  },
  {
    hash: '/issues/alerts/AL-0912',
    persona: 'Admin',
    caption:
      'One alert, with the cost of doing nothing against the cost of acting now.',
    breadcrumb: [
      { label: 'Home', hash: '/' },
      { label: 'Issues', hash: '/issues/alerts', static: true },
      { label: 'Alerts', hash: '/issues/alerts' },
      { label: 'AL-0912', hash: '/issues/alerts/AL-0912' },
    ],
  },
  {
    hash: '/machines/J-04',
    persona: 'Admin',
    caption:
      'The machine behind it — condition, history, and the records the answer came from.',
    breadcrumb: [
      { label: 'Home', hash: '/' },
      { label: 'Fleet health', hash: '/fleet' },
      { label: 'J-04', hash: '/machines/J-04' },
    ],
  },
  {
    hash: '/costs',
    persona: 'Admin',
    caption:
      'Back up to the money: cost to serve by division, site, and class — the case for getting ahead of the curve.',
    breadcrumb: [
      { label: 'Home', hash: '/' },
      { label: 'Cost summary', hash: '/costs' },
    ],
  },
];

// Resolve a path+search to a step index. Returns -1 if no match.
export function findStepByHash(hash: string): number {
  return STEPS.findIndex((s) => s.hash === hash);
}
